import type { Message } from '@/service/api'

/**
 * Returns true when the incoming reasoning_path snapshot carries new
 * information worth re-rendering. Preserving the existing reference when
 * nothing changed prevents ReasoningPath from re-rendering on every
 * text-delta SSE frame.
 *
 * Checks:
 * - status change
 * - step count change
 * - number of completed steps across the whole array (catches intermediate
 *   step completions that don't add a new step)
 * - last step description change (live step text growing)
 */
export function hasReasoningPathChanged(
  prev: Message['reasoning_path'],
  next: Message['reasoning_path'],
): boolean {
  if (!prev || !next) return prev !== next
  if (prev.status !== next.status) return true
  if (!Array.isArray(prev.steps) || !Array.isArray(next.steps)) return true
  if (prev.steps.length !== next.steps.length) return true
  const prevCompleted = prev.steps.filter((s) => s.completed).length
  const nextCompleted = next.steps.filter((s) => s.completed).length
  if (prevCompleted !== nextCompleted) return true
  const prevLast = prev.steps[prev.steps.length - 1]
  const nextLast = next.steps[next.steps.length - 1]
  if (!prevLast || !nextLast) return true
  return prevLast.description !== nextLast.description
}
